'use client';

/**
 * ObeyaCostPlanRfqsCard — searchable/filterable table of a cost plan's
 * linked RFQs/POs, with which of the plan's own lines each one covers.
 *
 * Slices the SAME `costplan_details` payload the KPI row, Lines table,
 * and the obeya_costplan mega-card already fetch. "Covers" — which of
 * the plan's own lines this RFQ's product_sids match — is now BFF-
 * computed (`linked_rfqs[].covers`, see `get_costplan_details`). Was
 * re-derived here client-side (a Set + filter/some cross-join) — found
 * in a session-wide sweep to be the same product_sid correlation
 * `reaches_rfq` already computes server-side, just at per-doc grain
 * instead of per-line, growing its own independent derivation of one
 * fact instead of reading it.
 *
 * RFQ, vendor name, and each covered product are click targets, same
 * selectNode() mechanism the Lines table's product/vendor cells use —
 * flips the rail's contentSelection to 'rfq'/'vendor'/'product' without
 * touching the canvas. "Covers this plan" collapses to a count by
 * default (same disclosure pattern as Room Comparison's cells) — a row
 * of 20+ product names used to render as one unbroken, unclickable
 * paragraph.
 * `vendor_sid` (added to load_costplan_linked_rfqs) is only the FIRST
 * invited vendor's sid — one click target, not a list; the rare
 * multi-vendor RFQ still shows every name in vendor_name.
 *
 * @see klyde_edge/ui_config/composition/cards/obeya.py — content_key="obeya_costplan_rfqs"
 * @see klyde_edge/klyde_intelligence/projects/queries.py — load_costplan_linked_rfqs vendor_sid
 *
 * @component_role: Card
 * @component_type: Card Renderer
 * @domain: Canvas / Obeya
 * @layer: presentation
 */

import { useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import { text } from '@/lib/design/tokens';
import { cn } from '@/lib/utils/cn';
import { useStrategyData } from '@/lib/api/hooks';
import { useViewportSelection } from '@/lib/stores/viewport-selection';
import { useGraphSelectionStore } from '@/lib/stores/graph-selection-store';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import { useLanguageStore } from '@/lib/stores/language-store';
import { useCardExport } from '@/lib/design/card-export';
import { useCardIdentity } from '@/lib/design/card-chrome';

interface LinkedDocLine {
  product_sid: string | null;
}

interface CoverItem {
  product_sid: string | null;
  product_name: string | null;
}

interface LinkedDoc {
  semantic_id: string | null;
  name: string | null;
  status: string | null;
  doc_type: string | null;
  vendor_name: string | null;
  vendor_sid: string | null;
  line_items: LinkedDocLine[];
  covers: CoverItem[];
}

interface CostPlanRfqsData {
  linked_rfqs: LinkedDoc[];
  labels?: Record<string, string>;
}

function statusClass(status: string | null): string {
  if (status === 'sent') return 'bg-green-500/10 text-green-500';
  if (status === 'draft') return 'bg-amber-500/10 text-amber-500';
  return 'bg-muted text-muted-foreground';
}

export function ObeyaCostPlanRfqsCard() {
  const { mcpId } = useViewportSelection();
  const { contentKey } = useCardIdentity();
  const strategy = useCapabilityStore((s) => s.strategyName('cost_plans', 'detail'));
  const lang = useLanguageStore((s) => s.lang);
  const { data: raw, isLoading } = useStrategyData<CostPlanRfqsData>(
    strategy, { semantic_id: mcpId ?? '', ...(lang ? { lang } : {}) }, !!mcpId && !!strategy,
  );
  const d = raw?.data;
  // Chrome labels ride the SAME costplan_details payload as the RFQ
  // list — BFF-sourced (_obeya_labels), not a second obeya/list fetch.
  const L = d?.labels ?? {};

  const selectNode = useGraphSelectionStore((s) => s.selectNode);
  const selectedId = useGraphSelectionStore((s) => s.selectedNode?.id);
  const selectRfq = (r: LinkedDoc) => {
    if (!r.semantic_id) return;
    selectNode({ id: r.semantic_id, label: r.name ?? 'RFQ', type: 'rfq', properties: {} });
  };
  const selectVendor = (r: LinkedDoc) => {
    if (!r.vendor_sid) return;
    selectNode({ id: r.vendor_sid, label: r.vendor_name ?? 'Vendor', type: 'vendor', properties: {} });
  };
  const selectCoverItem = (item: CoverItem) => {
    if (!item.product_sid) return;
    selectNode({ id: item.product_sid, label: item.product_name ?? 'Product', type: 'product', properties: {} });
  };

  const [query, setQuery] = useState('');
  // Which rows' "Covers this plan" list is expanded — collapsed
  // (count only) by default, same disclosure pattern as Room
  // Comparison's cells. A row of 28 product names as one unbroken
  // paragraph was unreadable and had nothing to click.
  const [expandedCovers, setExpandedCovers] = useState<Set<string>>(new Set());
  const toggleCovers = (key: string) => {
    setExpandedCovers((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  // covers_names is BFF-computed now (get_costplan_details) — this is
  // just the search filter over the already-shaped list, no re-derivation.
  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    const docs = d?.linked_rfqs ?? [];
    return q
      ? docs.filter((r) =>
          (r.name ?? '').toLowerCase().includes(q) || (r.vendor_name ?? '').toLowerCase().includes(q))
      : docs;
  }, [d?.linked_rfqs, query]);

  const queryKey = ['strategy-data', strategy, { semantic_id: mcpId ?? '', ...(lang ? { lang } : {}) }] as const;
  useCardExport(useMemo(() => ({
    content_key: contentKey ?? 'obeya_costplan_rfqs',
    status: (d ? 'loaded' : mcpId && strategy ? 'loading' : 'idle') as 'loaded' | 'loading' | 'idle',
    query_key: mcpId && strategy ? queryKey : null,
    text: `Cost plan linked RFQs: ${d?.linked_rfqs?.length ?? 0}`,
    state: { query },
    json: null,
  }), [contentKey, d, mcpId, strategy, queryKey, query]));

  if (!mcpId) {
    return (
      <div className="flex items-center justify-center h-full text-xs text-muted-foreground">
        Select a cost plan
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <div className="flex items-center gap-2 p-3 border-b border-border shrink-0">
        <div className="flex items-center gap-2 bg-muted/50 border border-border rounded-md px-2.5 py-1.5 flex-1 max-w-sm">
          <Search className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={L.search_rfqs_placeholder ?? ''}
            className="bg-transparent text-xs outline-none w-full"
          />
        </div>
        <span className={cn(text.muted, 'ml-auto')}>{rows.length} / {d?.linked_rfqs?.length ?? 0}</span>
      </div>
      <div className="flex-1 overflow-auto">
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-card z-10">
            <tr>
              <th className={cn('px-3 py-2 border-b border-border text-left', text.eyebrow)}>RFQ</th>
              <th className={cn('px-3 py-2 border-b border-border text-left', text.eyebrow)}>{L.col_vendor ?? ''}</th>
              <th className={cn('px-3 py-2 border-b border-border text-left', text.eyebrow)}>{L.col_status ?? ''}</th>
              <th className={cn('px-3 py-2 border-b border-border text-right', text.eyebrow)}>{L.col_lines ?? ''}</th>
              <th className={cn('px-3 py-2 border-b border-border text-left', text.eyebrow)}>{L.col_covers ?? ''}</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && !d ? (
              <tr><td colSpan={5} className={cn('px-3 py-6 text-center', text.muted)}>…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={5} className={cn('px-3 py-6 text-center', text.muted)}>{L.no_rfqs_match ?? ''}</td></tr>
            ) : rows.map((r) => {
              const isSelected = !!selectedId && (selectedId === r.semantic_id || selectedId === r.vendor_sid);
              return (
              <tr
                key={r.semantic_id}
                className={cn(
                  'border-b border-border/60',
                  isSelected ? 'bg-primary/10 shadow-[inset_2px_0_0_hsl(var(--primary))]' : 'hover:bg-muted/40',
                )}
              >
                <td className="px-3 py-2">
                  <button
                    type="button" onClick={() => selectRfq(r)} disabled={!r.semantic_id}
                    className={cn(text.title, 'hover:text-primary hover:underline disabled:no-underline disabled:cursor-default text-left')}
                  >
                    {r.name ?? '—'}
                  </button>
                </td>
                <td className="px-3 py-2">
                  <button
                    type="button" onClick={() => selectVendor(r)} disabled={!r.vendor_sid}
                    className={cn(text.body, 'hover:text-primary hover:underline disabled:no-underline disabled:cursor-default text-left')}
                  >
                    {r.vendor_name ?? '—'}
                  </button>
                </td>
                <td className="px-3 py-2">
                  <span className={cn('inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold', statusClass(r.status))}>
                    {r.status ?? '—'}
                  </span>
                </td>
                <td className="px-3 py-2 text-right tabular-nums">{r.line_items?.length ?? 0}</td>
                <td className="px-3 py-2 align-top">
                  {r.covers.length === 0 ? (
                    <span className={text.muted}>{L.covers_none ?? ''}</span>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={() => r.semantic_id && toggleCovers(r.semantic_id)}
                        className={cn(text.body, 'hover:text-primary')}
                      >
                        {r.covers.length} item{r.covers.length !== 1 ? 's' : ''}
                      </button>
                      {r.semantic_id && expandedCovers.has(r.semantic_id) && (
                        <div className="mt-1 flex flex-wrap gap-x-1.5 gap-y-1">
                          {r.covers.map((item, i) => (
                            <button
                              key={item.product_sid ?? i}
                              type="button"
                              disabled={!item.product_sid}
                              onClick={() => selectCoverItem(item)}
                              className={cn(
                                text.muted,
                                'rounded-full bg-muted px-2 py-0.5 text-left disabled:cursor-default',
                                item.product_sid && 'hover:text-primary hover:bg-primary/10',
                              )}
                            >
                              {item.product_name ?? '—'}
                            </button>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
